/* Native forms remain usable without JavaScript. */
(() => {
  document.documentElement.classList.add('js');
  const initializeAccountLinks = (root) => root.querySelectorAll('[data-header-login]').forEach(link => {
    const url = new URL(link.href, location.origin);
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    const destination = `${link.dataset.root || '/'}?view=account${/^\d{1,30}$/.test(preview || '') ? `&preview_theme_id=${preview}` : ''}`;
    url.searchParams.set('return_to', destination);
    link.href = url.href;
  });
  initializeAccountLinks(document);
  document.addEventListener('shopify:section:load', event => initializeAccountLinks(event.target));
  const mobileHeader = matchMedia('(max-width: 1023px)');
  const productPage = document.body.dataset.pageType === 'product';
  const updateMobilePurchaseBar = () => {
    if (!productPage) return;
    const headerBottom = Math.max(0, document.querySelector('.site-header')?.getBoundingClientRect().bottom || 0);
    document.querySelectorAll('[data-product-section]').forEach((section) => {
      const mainButton = section.querySelector('.product-form [data-purchase-button]');
      const bar = section.querySelector('.product-mobile-bar');
      if (!mainButton || !bar) return;
      // Do not show it above the main purchase area, or while that button is visible.
      const show = innerWidth < 750 && mainButton.getBoundingClientRect().bottom <= headerBottom;
      bar.toggleAttribute('data-visible', show); bar.inert = !show;
    });
  };
  let lastHeaderScroll = Math.max(0, window.scrollY), headerFrame = 0;
  const updateHeaderBackground = () => {
    headerFrame = 0;
    // Clamp elastic overscroll so bouncing at either end cannot reverse the header.
    const current = Math.min(Math.max(0, window.scrollY), Math.max(0, document.documentElement.scrollHeight - innerHeight));
    document.documentElement.toggleAttribute('data-header-scrolled', current > 0);
    if (productPage || !mobileHeader.matches || current === 0 || document.querySelector('.mobile-menu[open]')) {
      document.documentElement.removeAttribute('data-header-hidden');
    } else if (current !== lastHeaderScroll) {
      document.documentElement.toggleAttribute('data-header-hidden', current > lastHeaderScroll);
    }
    lastHeaderScroll = current;
    updateMobilePurchaseBar();
  };
  const scheduleHeader = () => { if (!headerFrame) headerFrame = requestAnimationFrame(updateHeaderBackground); };
  window.addEventListener('scroll', scheduleHeader, { passive: true });
  window.addEventListener('pageshow', updateHeaderBackground);
  window.addEventListener('resize', scheduleHeader, { passive: true });
  mobileHeader.addEventListener('change', scheduleHeader);
  updateHeaderBackground();
  document.addEventListener('keydown', (event) => {
    if (event.code === 'Space' && event.target.matches('[data-trust-marquee]')) {
      event.preventDefault(); event.target.toggleAttribute('data-motion-paused');
    }
  });
  // A true backdrop tap dismisses the checklist; scrolling or dragging out of it does not.
  let backdropChecklist = null;
  const outsideChecklist = event => {
    const dialog = event.target.closest?.('dialog.checklist-dialog[open]');
    if (!dialog || event.target !== dialog) return null;
    const rect = dialog.getBoundingClientRect();
    return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom ? dialog : null;
  };
  document.addEventListener('pointerdown', event => { backdropChecklist = outsideChecklist(event); });
  document.addEventListener('pointercancel', () => { backdropChecklist = null; });
  document.addEventListener('click', event => {
    const dialog = outsideChecklist(event), started = backdropChecklist;
    backdropChecklist = null;
    if (dialog && dialog === started) {
      event.preventDefault(); event.stopPropagation(); dialog.close();
    }
  }, true);
  const requests = new WeakMap();
  const sidebars = new WeakMap();
  const initializeSidebars = (root) => root.querySelectorAll('.product-sidebar').forEach((sidebar) => {
    if (sidebars.has(sidebar) || !('ResizeObserver' in window)) return;
    const observer = new ResizeObserver(([entry]) => {
      // Let tall, expanded FAQs scroll fully into view before the column sticks.
      const height = entry.borderBoxSize?.[0]?.blockSize || sidebar.getBoundingClientRect().height;
      sidebar.style.setProperty('--sidebar-height', `${Math.ceil(height)}px`);
      sidebar.dataset.stickyReady = '';
      updateMobilePurchaseBar();
    });
    observer.observe(sidebar); sidebars.set(sidebar, observer);
  });
  const removeSidebars = (root) => root.querySelectorAll('.product-sidebar').forEach((sidebar) => {
    sidebars.get(sidebar)?.disconnect(); sidebars.delete(sidebar);
  });
  const initializeGallery = (root) => {
    initializeSidebars(root);
    updateMobilePurchaseBar();
    root.querySelectorAll('[data-media-gallery]').forEach((gallery) => {
      const first = gallery.querySelector('.product-media');
      if (!first) return;
      first.dataset.active = '';
      gallery.dataset.galleryReady = '';
      gallery.querySelectorAll('.media-links a').forEach((link) => {
        if (link.hash === `#${first.id}`) link.setAttribute('aria-current', 'true');
      });
    });
  };
  initializeGallery(document);
  document.addEventListener('shopify:section:load', (event) => initializeGallery(event.target));
  document.addEventListener('shopify:section:unload', (event) => removeSidebars(event.target));

  const menus = new WeakMap();
  const initializeMenus = (root) => root.querySelectorAll('.mobile-menu').forEach((menu) => {
    if (menus.has(menu)) return;
    const panel = menu.querySelector('.mobile-menu-panel');
    const summary = menu.querySelector('summary');
    let background = [];
    const restoreBackground = () => {
      background.forEach(([element, inert]) => { element.inert = inert; }); background = [];
    };
    menu.addEventListener('toggle', () => {
      scheduleHeader();
      if (menu.open) {
        panel.setAttribute('role', 'dialog'); panel.setAttribute('aria-modal', 'true');
        background = [...document.querySelectorAll('main, .site-footer')].map((element) => [element, element.inert]);
        background.forEach(([element]) => { element.inert = true; });
        panel.querySelector('input')?.focus({ preventScroll: true });
      } else {
        panel.removeAttribute('role'); panel.removeAttribute('aria-modal');
        restoreBackground();
      }
    });
    panel.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const targets = [...panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled])')].filter((element) => element.getClientRects().length);
      const first = targets[0], last = targets[targets.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    });
    menu.querySelector('[data-menu-close]').addEventListener('click', () => { menu.open = false; summary.focus(); });
    const wide = matchMedia('(min-width: 1024px)');
    const onResize = () => { if (wide.matches) menu.open = false; };
    wide.addEventListener('change', onResize);
    menus.set(menu, () => { restoreBackground(); wide.removeEventListener('change', onResize); });
  });
  initializeMenus(document);
  document.addEventListener('shopify:section:load', (event) => initializeMenus(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('.mobile-menu').forEach((menu) => { menus.get(menu)?.(); menus.delete(menu); });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const details = event.target.closest('details[open]');
    if (details) {
      details.open = false;
      details.querySelector('summary')?.focus();
    }
  });

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      document.getElementById(opener.dataset.dialogOpen)?.showModal();
      return;
    }
    const closer = event.target.closest('[data-dialog-close]');
    if (closer) { closer.closest('dialog')?.close(); return; }
    const thumbnail = event.target.closest('.media-links a');
    if (thumbnail) {
      const gallery = thumbnail.closest('[data-media-gallery]');
      const media = document.getElementById(thumbnail.hash.slice(1));
      if (gallery && media && gallery.contains(media)) {
        event.preventDefault();
        gallery.querySelectorAll('.product-media').forEach((item) => {
          item.toggleAttribute('data-active', item === media);
          if (item !== media) item.querySelectorAll('video').forEach((video) => video.pause());
        });
        gallery.querySelectorAll('.media-links a').forEach((link) => {
          if (link === thumbnail) link.setAttribute('aria-current', 'true');
          else link.removeAttribute('aria-current');
        });
      }
      return;
    }
    const stepButton = event.target.closest('[data-quantity-step]');
    if (stepButton) {
      const input = stepButton.closest('.quantity-control')?.querySelector('input');
      if (!input) return;
      if (stepButton.dataset.quantityStep === '1') input.stepUp();
      else input.stepDown();
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    document.querySelectorAll('.mobile-menu[open], .nav-disclosure[open], .filter-group[open]').forEach((details) => {
      if (!details.contains(event.target)) details.open = false;
    });
    if (event.target.closest('.mobile-menu a')) {
      event.target.closest('.mobile-menu').open = false;
    }
  });

  document.addEventListener('change', async (event) => {
    const select = event.target.closest('[data-variant-select]');
    if (!select) return;
    if (select.value === 'custom') return;
    const section = select.closest('[data-product-section]');
    const form = select.closest('form');
    if (!section || !form) return;
    const prior = requests.get(section);
    prior?.abort();
    const controller = new AbortController();
    requests.set(section, controller);
    const destination = new URL(form.action, window.location.origin);
    new FormData(form).forEach((value, key) => {
      if (String(value)) destination.searchParams.set(key, String(value));
    });
    const fetchUrl = new URL(destination);
    fetchUrl.searchParams.set('section_id', section.dataset.sectionId);
    const status = section.querySelector('[data-selection-status]');
    if (status) status.textContent = status.dataset.loading;
    section.setAttribute('aria-busy', 'true');
    const buyButtons = section.querySelectorAll('[data-purchase-button]');
    buyButtons.forEach((button) => { button.disabled = true; });
    try {
      const response = await fetch(fetchUrl, { signal: controller.signal });
      if (!response.ok) throw new Error('Selection request failed');
      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const replacement = documentFragment.getElementById(section.id);
      if (!replacement || !replacement.querySelector('.product-form')) throw new Error('Product section missing');
      const focusId = select.id;
      removeSidebars(section);
      section.replaceWith(replacement);
      initializeGallery(replacement);
      history.replaceState({}, '', destination);
      replacement.dispatchEvent(new CustomEvent('piranha:product:updated', { bubbles: true }));
      document.getElementById(focusId)?.focus({ preventScroll: true });
    } catch (error) {
      if (error.name === 'AbortError') return;
      section.removeAttribute('aria-busy');
      if (status) status.textContent = status.dataset.error;
      // Keep the old variant from being purchased after a failed selection update.
      buyButtons.forEach((button) => { button.disabled = true; });
      const apply = form.querySelector('.variant-apply');
      if (apply) apply.style.display = 'inline-flex';
    } finally {
      if (requests.get(section) === controller) {
        requests.delete(section);
        section.removeAttribute('aria-busy');
      }
    }
  });
})();
